import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link as MuiLink,
  Paper,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import type { Control, Resolver } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CHANNEL_LABEL,
  NOTIFICATION_CODES,
  NOTIFICATION_LIST_KEY,
  RECORD_STATUSES,
  SMS_TARGETS,
  US_STATES,
  eventsFor,
  getReportDef,
  notificationInputSchema,
  sampleSendSchema,
  smsSegments,
  variablesFor,
} from '@app/shared';
import type {
  CarrierDto,
  NotificationChannel,
  NotificationInput,
  NotificationTemplateDto,
  ReportRow,
  SampleSendInput,
  SpfCheckDto,
} from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState, LoadingState } from '../../components/PageState';
import { FormDialog } from '../../components/dialogs';
import {
  MultiSelectInput,
  SelectInput,
  TextInput,
  applyServerErrors,
  toOptions,
} from '../../components/form';
import { api } from '../../lib/api';
import { useFeatureReleased } from '../../release';
import { Can, RequirePermission } from '../auth/guards';
import { ReportScreen, reportQueryKey } from '../reports/ReportPage';

const listPath = (channel: NotificationChannel) => `/notifications/${channel}`;
const ADD_TITLE: Record<NotificationChannel, string> = {
  email: 'Add Email Notification',
  sms: 'Add SMS Notification',
  ivr: 'Add New IVR Notification',
};
const STATE_OPTIONS = US_STATES.map(([code, name]) => ({
  value: code,
  label: `${code} - ${name}`,
}));

type FormValues = Omit<NotificationInput, 'channel'>;
const EMPTY: FormValues = {
  eventName: '',
  categoryName: '',
  carriers: [],
  states: [],
  fromName: '',
  fromEmail: '',
  bccEmail: '',
  replyTo: '',
  smsName: '',
  subject: '',
  body: '',
  status: 'Active',
  smsTarget: 'Primary Number',
  restrictChildAccount: 'No',
};

/** Labels of the text fields, as the reference's add forms word them. */
const TEXT_LABEL: Record<NotificationChannel, string> = {
  email: 'Email Text (in English)',
  sms: 'SMS Text (in English)',
  ivr: 'Message',
};

/** Example values for "Preview"; a variable without one is shown as it was typed. */
const PREVIEW_VALUES: Record<string, string> = {
  ACCOUNT: '100245',
  FIRST_NAME: 'Jordan',
  LAST_NAME: 'Avery',
  MAIL_TELEPHONE: '5550100200',
  COMPANY_NAME: 'Your company',
  ENROLLMENT_ID: 'LC1024',
  EMAIL_ADDRESS: 'jordan.avery@example.test',
  COPYRIGHT_YEAR: String(new Date().getFullYear()),
  ADDRESS: '12 Alder Way, Columbus, OH 43215',
  MDN: '5550100200',
  PLAN_NAME: 'Prepaid 5GB',
  CURRENT_ACCOUNT_STATUS: 'Active',
  PREVIOUS_ACCOUNT_STATUS: 'Dispatched',
  AMOUNT: '20.00',
  REFUNDED_AMOUNT: '10.00',
};
const preview = (text: string): string =>
  text.replace(/%([A-Z0-9_]+)%/g, (whole, key: string) => PREVIEW_VALUES[key] ?? whole);

function NotificationForm({
  channel,
  template,
}: {
  channel: NotificationChannel;
  template?: NotificationTemplateDto;
}) {
  const notify = useNotify();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const carriers = useQuery({
    queryKey: ['carriers', 'report-filter'],
    queryFn: () => api.get<{ data: CarrierDto[] }>('/carriers'),
    enabled: channel !== 'ivr',
  });
  const { control, handleSubmit, setError } = useForm<FormValues>({
    defaultValues: template
      ? {
          eventName: template.eventName,
          categoryName: template.categoryName,
          carriers: template.carriers,
          states: template.states as FormValues['states'],
          fromName: template.fromName,
          fromEmail: template.fromEmail,
          bccEmail: template.bccEmail,
          replyTo: template.replyTo,
          smsName: template.smsName,
          subject: template.subject,
          body: template.body,
          status: template.status,
          smsTarget: template.smsTarget,
          restrictChildAccount: template.restrictChildAccount,
        }
      : EMPTY,
    // The channel is fixed by the page; it is added before validating.
    resolver: ((values, context, options) =>
      zodResolver(notificationInputSchema)(
        { ...values, channel } as NotificationInput,
        context,
        options,
      )) as Resolver<FormValues>,
  });
  const body = useWatch({ control, name: 'body' }) ?? '';
  const subject = useWatch({ control, name: 'subject' }) ?? '';
  const eventName = useWatch({ control, name: 'eventName' }) ?? '';
  const count = smsSegments(body);
  const [previewing, setPreviewing] = useState(false);

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      template
        ? api.put<NotificationTemplateDto>(`/notifications/${template.id}`, { ...values, channel })
        : api.post<NotificationTemplateDto>('/notifications', { ...values, channel }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: reportQueryKey(NOTIFICATION_LIST_KEY[channel]) }),
        queryClient.invalidateQueries({ queryKey: ['notifications'] }),
      ]),
  });
  const submit = handleSubmit(async (values) => {
    try {
      await save.mutateAsync(values);
      notify.success(`${CHANNEL_LABEL[channel]} notification ${template ? 'updated' : 'added'}.`);
      navigate(listPath(channel));
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  return (
    <Paper
      variant="outlined"
      component="form"
      noValidate
      aria-label={template ? 'Update notification' : ADD_TITLE[channel]}
      onSubmit={(e) => void submit(e)}
      sx={{ p: 2, maxWidth: 1040 }}
    >
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          alignItems: 'start',
          mb: 2,
        }}
      >
        <SelectInput
          control={control}
          name="eventName"
          label="Notification Type"
          required
          emptyLabel="Select"
          options={toOptions(eventsFor(channel))}
        />
        <TextInput
          control={control}
          name="categoryName"
          label="Category Name"
          slotProps={{ htmlInput: { maxLength: 60 } }}
        />
        {channel === 'sms' ? (
          <TextInput
            control={control}
            name="smsName"
            label="SMS Name"
            required
            slotProps={{ htmlInput: { maxLength: 80 } }}
          />
        ) : null}
        {channel === 'email' ? (
          <>
            <TextInput control={control} name="fromName" label="From Name" />
            <Box>
              <TextInput
                control={control}
                name="fromEmail"
                label="From Email Address"
                required
                type="email"
                fullWidth
              />
              <SpfStatus control={control} />
            </Box>
            <TextInput control={control} name="bccEmail" label="BCC Email Address" type="email" />
            <TextInput control={control} name="replyTo" label="Reply To" type="email" />
          </>
        ) : null}
        {channel === 'ivr' ? null : (
          <>
            <MultiSelectInput
              control={control}
              name="carriers"
              label="Select Carrier"
              helperText="None chosen means All"
              options={(carriers.data?.data ?? []).map((x) => ({ value: x.code, label: x.code }))}
            />
            <MultiSelectInput
              control={control}
              name="states"
              label="State"
              helperText="None chosen means every state"
              options={STATE_OPTIONS}
            />
          </>
        )}
        {channel === 'sms' ? (
          <SelectInput
            control={control}
            name="smsTarget"
            label="Choose where to send SMS"
            options={toOptions(SMS_TARGETS)}
          />
        ) : null}
        <SelectInput
          control={control}
          name="status"
          label="Status"
          options={toOptions(RECORD_STATUSES)}
        />
        {channel === 'ivr' ? null : (
          <SelectInput
            control={control}
            name="restrictChildAccount"
            label="Restriction for child account"
            options={toOptions(['Yes', 'No'])}
          />
        )}
      </Box>
      {channel === 'email' ? (
        <Box sx={{ display: 'grid', mb: 2 }}>
          <TextInput
            control={control}
            name="subject"
            label="Email Subject (in English)"
            required
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
        </Box>
      ) : null}
      <Box sx={{ display: 'grid', mb: 1 }}>
        <TextInput
          control={control}
          name="body"
          label={TEXT_LABEL[channel]}
          required
          multiline
          minRows={channel === 'email' ? 8 : 4}
        />
      </Box>
      {channel === 'sms' ? (
        <Typography aria-label="Character count" sx={{ mb: 1 }}>
          {count.unicode ? 'Unicode' : 'GSM'}: {count.length} characters, {count.segments} SMS
        </Typography>
      ) : null}
      <Typography color="text.secondary" aria-label="Variables" sx={{ mb: 2 }}>
        To include a variable, embed its name in the text: {variablesFor(eventName).join(' ')}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button type="submit" variant="contained" disabled={save.isPending}>
          Submit
        </Button>
        {channel === 'ivr' ? null : (
          <Button variant="outlined" onClick={() => setPreviewing(true)} disabled={!body.trim()}>
            Preview
          </Button>
        )}
        <Button component={Link} to={listPath(channel)} disabled={save.isPending}>
          Cancel
        </Button>
      </Box>
      <Dialog open={previewing} onClose={() => setPreviewing(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Preview</DialogTitle>
        <DialogContent dividers>
          <Typography color="text.secondary" sx={{ mb: 1.5 }}>
            Shown with example values in place of the variables.
          </Typography>
          {channel === 'email' ? (
            <Typography sx={{ fontWeight: 600, mb: 1 }}>{preview(subject)}</Typography>
          ) : null}
          <Typography aria-label="Preview text" sx={{ whiteSpace: 'pre-wrap' }}>
            {preview(body)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewing(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

/** Add email / SMS / IVR notification (PC668, PC675, PC832). */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * "SPF record is valid / SPF record is not valid" under the From Email Address (switch
 * `notification-spf-check`): the domain of the address is looked up once the typing stops.
 */
function SpfStatus({ control }: { control: Control<FormValues> }) {
  const on = useFeatureReleased()('notification-spf-check');
  const typed = (useWatch({ control, name: 'fromEmail' }) ?? '').trim();
  const [email, setEmail] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setEmail(EMAIL.test(typed) ? typed : ''), 600);
    return () => clearTimeout(timer);
  }, [typed]);
  const spf = useQuery({
    queryKey: ['notifications', 'spf', email.toLowerCase()],
    queryFn: () => api.post<SpfCheckDto>('/notifications/spf-check', { email }),
    enabled: on && email !== '',
    staleTime: 5 * 60_000,
  });
  if (!on || !email || !spf.data) return null;
  return (
    <Typography
      variant="body2"
      role="status"
      sx={{ mt: 0.5, color: spf.data.valid ? 'success.main' : 'error.main' }}
    >
      {spf.data.valid ? 'SPF record is valid' : 'SPF record is not valid'}
    </Typography>
  );
}

export function NotificationAddPage({ channel }: { channel: NotificationChannel }) {
  const codes = NOTIFICATION_CODES[channel];
  return (
    <RequirePermission code={codes.add}>
      <Box>
        <PageHeader title={ADD_TITLE[channel]} code={codes.add} />
        <NotificationForm channel={channel} />
      </Box>
    </RequirePermission>
  );
}

/** Changes a notification; reached from the Action column of its list. */
export function NotificationEditPage({ channel }: { channel: NotificationChannel }) {
  const { id } = useParams();
  const codes = NOTIFICATION_CODES[channel];
  const template = useQuery({
    queryKey: ['notifications', id],
    queryFn: () => api.get<NotificationTemplateDto>(`/notifications/${id}`),
    enabled: Boolean(id),
  });
  return (
    <RequirePermission code={codes.view}>
      <Box>
        <PageHeader title={`Update ${CHANNEL_LABEL[channel]} Notification`} code={codes.view} />
        {template.error ? (
          <ErrorState error={template.error} onRetry={() => void template.refetch()} />
        ) : !template.data ? (
          <LoadingState />
        ) : template.data.channel !== channel ? (
          <Alert severity="warning">This notification does not exist.</Alert>
        ) : (
          <NotificationForm key={template.data.id} channel={channel} template={template.data} />
        )}
      </Box>
    </RequirePermission>
  );
}

/** "Sample Email Notification" / "Sample SMS Notification". */
function SampleDialog({
  channel,
  row,
  onClose,
}: {
  channel: NotificationChannel;
  row: ReportRow | null;
  onClose: () => void;
}) {
  const notify = useNotify();
  const { control, handleSubmit, reset, setError } = useForm<SampleSendInput>({
    defaultValues: { customerId: '', to: '' },
    resolver: zodResolver(sampleSendSchema),
  });
  useEffect(() => {
    if (row) reset({ customerId: '', to: '' });
  }, [row, reset]);
  const sample = useMutation({
    mutationFn: (input: SampleSendInput) =>
      api.post<{ status: string }>(`/notifications/${String(row!.id)}/sample`, input),
  });
  const submit = handleSubmit(async (values) => {
    try {
      const done = await sample.mutateAsync(values);
      if (done.status === 'Success') notify.success('Sample sent.');
      else notify.error(new Error('The sample could not be delivered.'));
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  const email = channel === 'email';
  return (
    <FormDialog
      open={Boolean(row)}
      title={`Sample ${CHANNEL_LABEL[channel]} Notification`}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={sample.isPending}
      submitLabel="Send"
      maxWidth="xs"
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
        <Typography>Event Name: {row ? String(row.eventName) : ''}</Typography>
        <TextInput
          control={control}
          name="customerId"
          label="Customer ID"
          required
          autoFocus
          helperText="The message is filled in with this customer's values"
          slotProps={{ htmlInput: { maxLength: 11, inputMode: 'numeric' } }}
        />
        <TextInput
          control={control}
          name="to"
          label={email ? 'To Email' : 'Telephone Number'}
          required
          type={email ? 'email' : 'text'}
          slotProps={{ htmlInput: { maxLength: email ? 120 : 10 } }}
        />
      </Box>
    </FormDialog>
  );
}

/** View email / SMS / IVR notifications (PC669, PC676, PC833). */
export function NotificationListPage({ channel }: { channel: NotificationChannel }) {
  const [sampling, setSampling] = useState<ReportRow | null>(null);
  const codes = NOTIFICATION_CODES[channel];
  return (
    <>
      <ReportScreen
        def={getReportDef(NOTIFICATION_LIST_KEY[channel])!}
        headerActions={
          <Can code={codes.add}>
            <Button component={Link} to={`${listPath(channel)}/new`} variant="contained">
              {ADD_TITLE[channel]}
            </Button>
          </Can>
        }
        rowActions={(row) => (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', whiteSpace: 'nowrap' }}>
            <MuiLink
              component={Link}
              to={`${listPath(channel)}/${String(row.id)}`}
              aria-label={`Edit ${String(row.eventName)}`}
            >
              Edit
            </MuiLink>
            {channel === 'ivr' ? null : (
              <Button
                size="small"
                aria-label={`Sample ${String(row.eventName)}`}
                onClick={() => setSampling(row)}
              >
                Sample
              </Button>
            )}
          </Box>
        )}
      />
      <SampleDialog channel={channel} row={sampling} onClose={() => setSampling(null)} />
    </>
  );
}
